import { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { InputText } from '../../../components/Inputs/InputText';
import { LoadingAbsolute } from '../../../components/LoadingAbsolute';
import { useApi } from '../../../providers/ApiProvider';
import { DropdownRessource, ModeFormulaire, Ressource } from '../../../types';
import { InputSwitch } from '../../../components/Inputs/InputSwitch';
import { Filiere, Metier } from '../../../types/schematics';
import { GoodApiResponse, ErrorApiResponse, BaseIndexParams, PaginatedApiResponse } from '../../../types/api';
import { Config } from '../../../config/config';
import { Helmet } from 'react-helmet';
import { InputTextarea } from '../../../components/Inputs/InputTextarea';
import { InputImage } from '../../../components/Inputs/InputImage';
import { ReadOnlyNumber } from '../../../components/Inputs/ReadOnlyNumber';
import { handleError } from '../../../utils/ErrorHandler';
import { ArrowUturnLeftIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { Tab, TabPanel, Tabs, TabsBody, TabsHeader } from '../../../components/Onglets';
import { ImageHints } from '../../../components/ImageHints';
import { slugify } from '../../../utils/ChangeText';
import { ReadOnlyText } from '../../../components/Inputs/ReadOnlyText';
import { SubmitSave } from '../../../components/SubmitSave';

type Props = { modeFormulaire: ModeFormulaire };

/**
 * Ce composant doit afficher un formulaire qui à 3 modes de fonctionnement. Les 2 principaux sont la création et l'édition.
 * On a également un mode lecture seule. L'idée c'est de gérer une partie du CRUD.
 * C'est dans ce composant que vont se faire les appels API pour créer, éditer et voir une ressource en particulier.
 *
 * @param modeFormulaire - Mode du formulaire (creer, editer et voir).
 * @returns JSX du composant
 */
export const AdminMetierFormScreen: FunctionComponent<Props> = ({ modeFormulaire }) => {
  // On initialise les données dont on va avoir besoin sur cette page

  /**
   * On stocke si on est en train d'utiliser l'API ou non.
   */
  const [apiIsLoading, setApiIsLoading] = useState(false);

  /**
   * On stocke l'état des filières.
   */
  const [filieres, setFilieres] = useState<DropdownRessource[] | null>(null);

  /**
   * On stocke les filères possédés par le métier.
   */
  const [ownedFilieres, setOwnedFilieres] = useState<DropdownRessource[] | null>(null);

  /**
   * On stocke l'état des formations.
   */
  const [formations, setFormations] = useState<DropdownRessource[] | null>(null);

  /**
   * On stocke les formations possédés par le site.
   */
  const [ownedFormations, setOwnedFormations] = useState<DropdownRessource[] | null>(null);

  /**
   * On stocke le nom afin de l'afficher en mode édition et voir.
   */
  const [nom, setNom] = useState<Metier['nom']>('');

  /**
   * On à besoin d'initialiser le client d'API à l'aide d'un hook personalisé.
   */
  const client = useApi();

  /**
   * Contient un état général des valeurs des champs du formulaire ainsi que les champs qui ont reçu une erreur lors de l'envoi du formulaire.
   */
  const [formInputs, setFormInputs] = useState<{ [K in keyof Metier]: { value: Metier[K]; error: boolean } }>({
    nom: { value: '', error: false },
    nom_commercial: { value: null, error: false },
    description_commerciale: { value: null, error: false },
    image_couverture: { value: null, error: false },
    slug: { value: '', error: false },
    est_visible: { value: false, error: false },
    yp_code: { value: null, error: false },
    created_at: { value: null, error: false },
  });

  const [imageCovertureString, setImageCovertureString] = useState<Metier['image_couverture'] | null>(null);

  /**
   * Permet au composant de changer de page.
   */
  const navigate = useNavigate();

  const params = useParams();

  // On a besoin du useEffect lorsque l'on est en mode édition et lecture pour précharger le formulaire avec les données de la ressource.

  useEffect(() => {
    switch (modeFormulaire) {
      case ModeFormulaire.creer:
        break;
      case ModeFormulaire.voir:
      case ModeFormulaire.editer:
        if (params.id === undefined) {
          navigate('/admin/metiers');
        }
        handleShow(parseInt(params.id as string));
        handleIndex();
        break;

      default:
        break;
    }
  }, [client]);

  /**
   * Retrouve Les ressources nécessaires pour afficher les dropdown et les stockent dans des états locaux.
   *
   * @param indexParams - Paramètres de la recherche.
   */
  const handleIndex = () => {
    setApiIsLoading(true);
    const baseParams: BaseIndexParams = { as_foreign: true, no_pagination: true };
    Promise.all([
      client.get<PaginatedApiResponse<Filiere>, BaseIndexParams>('/admin/filieres', { params: { ...baseParams, sort: 'nom_commercial' } }),
      client.get<PaginatedApiResponse<Filiere>>(`/admin/metiers/${parseInt(params.id as string)}/filieres`),
      client.get<PaginatedApiResponse<Filiere>, BaseIndexParams>('/admin/formations', {
        params: { ...baseParams, sort: 'nom_commercial', est_ecommerce: true },
      }),
      client.get<PaginatedApiResponse<Filiere>>(`/admin/metiers/${parseInt(params.id as string)}/formations`),
    ])
      .then((response) => {
        setFilieres(response[0].data.data as unknown as DropdownRessource[]);
        setOwnedFilieres(response[1].data.data as unknown as DropdownRessource[]);
        setFormations(response[2].data.data as unknown as DropdownRessource[]);
        setOwnedFormations(response[3].data.data as unknown as DropdownRessource[]);
      })
      .catch((error: ErrorApiResponse) => {
        handleError(error);
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  /**
   * Retrouve par le réseau la ressource liée à l'ID foruni et la met dans un état local.
   * Ne doit être utilisé qu'en mode édition et lecture seule.
   *
   * @param ressourceId - ID de la ressource dont on veut retrouver les informations.
   */
  const handleShow = (ressourceId: Ressource['id']) => {
    if (apiIsLoading === true) {
      return;
    }

    setApiIsLoading(true);
    client
      .get<GoodApiResponse<Metier>>(`/admin/metiers/${ressourceId}`)
      .then((reponse) => {
        const updatedInputs = Object.keys(reponse.data.data).reduce((acc, key) => {
          if (key === 'image_couverture' && reponse.data.data.image_couverture !== null) {
            setImageCovertureString(reponse.data.data.image_couverture);
          }
          return { ...acc, [key]: { value: reponse.data.data[key as keyof Metier], error: false } };
        }, formInputs);
        setFormInputs(updatedInputs);
        setNom(updatedInputs.nom.value);
      })
      .catch((error: ErrorApiResponse) => {
        handleError(error, () => {
          navigate('/admin/metiers');
        });
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  /**
   * Poste par le réseau une ressource crée dans le formulaire via un état local.
   * Ne doit être utilisé qu'en mode création.
   */
  const handleStore = () => {
    setApiIsLoading(true);

    let ressource: Partial<Metier> = {};
    Object.keys(formInputs).forEach((key) => {
      if (formInputs[key as keyof Metier]?.value !== null && !(key === 'image_couverture' && formInputs.image_couverture.value === imageCovertureString)) {
        ressource = { ...ressource, [key]: formInputs[key as keyof Metier]?.value };
      }
    });
    client
      .post<GoodApiResponse<Metier>, Metier>('/admin/metiers', ressource as Metier)
      .then(() => {
        toast.success(`Création éffectuée`);
        navigate('/admin/metiers');
      })
      .catch((error: ErrorApiResponse<Metier>) => {
        handleError(error, () => {
          let object = formInputs;
          Object.keys(error.response.data.errors).forEach((key) => {
            if (error.response.data.errors[key as keyof Metier]) {
              object = { ...object, [key]: { value: formInputs[key as keyof Metier]?.value, error: true } };
            }
          }, formInputs);
          setFormInputs(object);
        });
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  /**
   * Met à jour, par le réseau, une ressource à partir du formulaire via un état local.
   * Ne doit être utilisé qu'en mode édition.
   *
   * @param ressourceId - ID de la ressource dont on veut modifier les informations.
   */
  const handleUpdate = (ressourceId: Ressource['id']) => {
    setApiIsLoading(true);

    let ressource: Partial<Metier> = {};
    Object.keys(formInputs).forEach((key) => {
      if (formInputs[key as keyof Metier]?.value !== null && !(key === 'image_couverture' && formInputs.image_couverture.value === imageCovertureString)) {
        ressource = { ...ressource, [key]: formInputs[key as keyof Metier]?.value };
      }
    });

    client
      .patch<GoodApiResponse<Metier>>(`/admin/metiers/${ressourceId}`, ressource)
      .then(() => {
        toast.success(`Modification éffectuée`);
        navigate('/admin/metiers');
      })
      .catch((error: ErrorApiResponse<Metier>) => {
        handleError(error, () => {
          let object = formInputs;
          Object.keys(error.response.data.errors).forEach((key) => {
            if (error.response.data.errors[key as keyof Metier]) {
              object = { ...object, [key]: { value: formInputs[key as keyof Metier]?.value, error: true } };
            }
          }, formInputs);
          setFormInputs(object);
        });
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  /**
   * Cette méthode permet de gérer l'envoi du formulaire en fonction du mode dans lequel on se trouve.
   * L'envoi ne fait rien si on est en mode voir.
   *
   * @param event - Évenement de l'envoie.
   */
  const handleSubmit = (event: { preventDefault: () => void }): void => {
    event.preventDefault();

    if (apiIsLoading === true) {
      return;
    }

    if (modeFormulaire === ModeFormulaire.creer) {
      handleStore();
    }

    if (modeFormulaire === ModeFormulaire.editer) {
      if (params.id !== undefined) {
        handleUpdate(parseInt(params.id as string));
      }
    }
  };

  /**
   * Cette méthode permet de gérer les changements à l'interieur des champs du formulaire et de gérer les valeurs des champs via un état local.
   *
   * @param field - Champs du formulaire dont on doit stocker la valeur et s'il est en erreur.
   */
  const handleInputChange = (field: keyof Metier) => {
    return (event: { target: { value: string } }) => {
      setFormInputs((prev) => ({
        ...prev,
        [field]: { value: field === 'slug' ? slugify(event.target.value) : event.target.value, error: false },
      }));
    };
  };

  const handleSwitchChange = (field: keyof Metier) => {
    setFormInputs((prev) => ({
      ...prev,
      [field]: { value: !prev[field as keyof Metier]?.value, error: false },
    }));
  };

  const handleImageChange = (field: keyof Metier) => {
    return (imageString: string) => {
      setFormInputs((prev) => ({
        ...prev,
        [field]: { value: imageString, error: false },
      }));
    };
  };

  /**
   * Gère les chngement de Métiers.
   *
   * @param roleId - ID du métier dont on soihaite changer l'état pour la filière.
   */
  const handleFilieresAssignation = async (filiereId: Metier['id']) => {
    setApiIsLoading(true);
    try {
      if (ownedFilieres?.findIndex((ownedFiliere) => ownedFiliere.id === filiereId) === -1) {
        await client.post(`/admin/metiers/${parseInt(params.id as string)}/filieres/${filiereId}`);
        setOwnedFilieres([
          ...(ownedFilieres as DropdownRessource[]),
          { id: filiereId, label: (filieres as DropdownRessource[]).find((filiere) => filiere.id === filiereId)?.label as string },
        ]);
        toast.success(`Filière assignée au métier`);
      } else {
        await client.delete(`/admin/metiers/${parseInt(params.id as string)}/filieres/${filiereId}`);
        setOwnedFilieres((ownedFilieres as DropdownRessource[]).filter((ownedFiliere) => ownedFiliere.id !== filiereId));
        toast.success(`Filière retirée au métier`);
      }
    } catch (error: any) {
      if (error.response && parseInt(error.response.status) >= 400 && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.message);
      }
    }
    setApiIsLoading(false);
  };

  /**
   * Gère les chngement de Métiers.
   *
   * @param roleId - ID du métier dont on soihaite changer l'état pour la filière.
   */
  const handleFormationsAssignation = async (formationId: Metier['id']) => {
    setApiIsLoading(true);
    try {
      if (ownedFormations?.findIndex((ownedFormation) => ownedFormation.id === formationId) === -1) {
        await client.post(`/admin/metiers/${parseInt(params.id as string)}/formations/${formationId}`);
        setOwnedFormations([
          ...(ownedFormations as DropdownRessource[]),
          { id: formationId, label: (formations as DropdownRessource[]).find((formation) => formation.id === formationId)?.label as string },
        ]);
        toast.success(`Formation assignée au métier`);
      } else {
        await client.delete(`/admin/metiers/${parseInt(params.id as string)}/formations/${formationId}`);
        setOwnedFormations((ownedFormations as DropdownRessource[]).filter((ownedFormation) => ownedFormation.id !== formationId));
        toast.success(`Formation retirée au métier`);
      }
    } catch (error: any) {
      handleError(error);
    }
    setApiIsLoading(false);
  };

  /**
   * Paramètres tabs
   * @constant
   */
  const data = [
    {
      label: 'Filières',
      value: 'filieres',
    },
    {
      label: 'Formations',
      value: 'formations',
    },
  ] as const;

  if (modeFormulaire !== ModeFormulaire.creer && (filieres === null || ownedFilieres === null || formations === null || ownedFormations === null)) {
    return <Fragment>Chargement...</Fragment>;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Métier - {Config.app_label}</title>
      </Helmet>

      <div className='px-4 py-4 md:py-3 bg-gray-100 rounded-tl-lg rounded-tr-lg'>
        <div className='flex w-11/12 mx-auto xl:w-full xl:mx-0 justify-between'>
          <div className='flex flex-row items-center gap-3'>
            <Link className='flex-none' to='/admin/metiers'>
              <button className='mr-1 py-1 px-6 flex flex-row items-center rounded-full bg-secondary-1/10 text-gray-700'>
                <ArrowUturnLeftIcon className='h-4 w-4 inline mr-2 flex-none' />
                <span>Retour à la liste</span>
              </button>
            </Link>
            <p className='text-base sm:text-lg md:text-xl poppins-bold leading-normal text-secondary-1 pl-4'>{`Métier ${
              modeFormulaire !== ModeFormulaire.creer ? nom : ''
            }`}</p>
          </div>

          <Link to={`/metiers/${formInputs.slug.value}`} className='flex-none ml-3' target='_blank'>
            <button className='mr-1 py-1 px-6 flex flex-row items-center rounded-full bg-secondary-1 text-white'>
              <ClipboardDocumentListIcon className='h-4 w-4 inline mr-2 flex-none' />
              <span>Voir la page du metier</span>
            </button>
          </Link>
        </div>
      </div>

      <Tabs value='general'>
        <TabsHeader>
          <Tab value='general'>Général</Tab>
          {modeFormulaire !== ModeFormulaire.creer &&
            data.map(({ label, value }) => (
              <Tab key={value} value={value}>
                {label}
              </Tab>
            ))}
        </TabsHeader>
        <TabsBody>
          <TabPanel value='general'>
            <form className='relative' onSubmit={handleSubmit}>
              <div className='bg-white p-5'>
                <div className='container mx-auto bg-white rounded'>
                  <fieldset className='pb-7 mb-7 border-b'>
                    <legend className='block text-left mb-3 text-xl text-secondary-1'>Informations</legend>
                    <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 items-center'>
                      <div>
                        <ReadOnlyText label='Nom' value={formInputs.nom.value as string} error={formInputs.nom.error} />
                      </div>
                      <div>
                        <InputText
                          label='Nom commercial'
                          value={formInputs.nom_commercial.value as string}
                          onChange={handleInputChange('nom_commercial')}
                          error={formInputs.nom_commercial.error}
                        />
                      </div>
                      <div>
                        <InputText label='Slug' value={formInputs.slug.value as string} onChange={handleInputChange('slug')} error={formInputs.slug.error} />
                      </div>
                      <div>
                        <ReadOnlyNumber label='YParéo CodeTypeMatiere' value={formInputs.yp_code.value} error={formInputs.yp_code.error} />
                      </div>
                    </div>
                  </fieldset>
                  <fieldset className='pb-7 mb-7 border-b'>
                    <legend className='block text-left mb-3 text-xl text-secondary-1'>Descriptions</legend>
                    <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-2'>
                      <div>
                        <InputTextarea
                          label='Description commerciale'
                          value={formInputs.description_commerciale.value as string}
                          onChange={handleInputChange('description_commerciale')}
                          error={formInputs.description_commerciale.error}
                        />
                      </div>
                    </div>
                  </fieldset>
                  <fieldset className='pb-7 mb-7 border-b'>
                    <legend className='block text-left mb-3 text-xl text-secondary-1'>Image</legend>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                      <div>
                        <InputImage label='Image de couverture' imageUrl={imageCovertureString as string} onChange={handleImageChange('image_couverture')} />
                      </div>
                      <div className='pt-6'>
                        <ImageHints />
                      </div>
                    </div>
                  </fieldset>
                  <fieldset className='pb-7 mb-7 border-b'>
                    <legend className='block text-left mb-3 text-xl text-secondary-1'>Paramètres</legend>
                    <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 items-center'>
                      <div>
                        <InputSwitch
                          label='Est visible'
                          checked={formInputs.est_visible.value !== null ? formInputs.est_visible.value : false}
                          onChange={() => handleSwitchChange('est_visible')}
                        />
                      </div>
                    </div>
                  </fieldset>
                </div>

                <div className='container mx-auto w-11/12 xl:w-full'>
                  <div className='w-full py-4 sm:px-0 bg-white flex justify-end'>{modeFormulaire !== ModeFormulaire.voir && <SubmitSave />}</div>
                </div>
              </div>
              {apiIsLoading ? <LoadingAbsolute /> : ''}
            </form>
          </TabPanel>
          {modeFormulaire !== ModeFormulaire.creer && (
            <TabPanel value='filieres'>
              <div className='columns-1 sm:columns-2 xl:columns-3 gap-2 items-start bg-white p-5 rounded-b-lg'>
                {filieres && filieres.length > 0 ? (
                  filieres.map((filiere) => (
                    <div key={filiere.id} className='pb-2'>
                      <InputSwitch
                        label={filiere.label}
                        checked={ownedFilieres?.findIndex((ownedFiliere) => ownedFiliere.id === filiere.id) !== -1}
                        onChange={() => handleFilieresAssignation(filiere.id)}
                      />
                    </div>
                  ))
                ) : (
                  <div className='text-center px-4 py-7'>Il n'y pas de filière pour ce métier.</div>
                )}
              </div>
            </TabPanel>
          )}
          {modeFormulaire !== ModeFormulaire.creer && (
            <TabPanel value='formations'>
              <div className='columns-1 sm:columns-2 xl:columns-3 gap-2 items-start bg-white p-5 rounded-b-lg'>
                {formations && formations.length > 0 ? (
                  formations.map((formation) => (
                    <div key={formation.id} className='pb-2 break-inside-avoid'>
                      <InputSwitch
                        label={formation.label}
                        checked={ownedFormations?.findIndex((ownedFormation) => ownedFormation.id === formation.id) !== -1}
                        onChange={() => handleFormationsAssignation(formation.id)}
                      />
                    </div>
                  ))
                ) : (
                  <div className='text-center px-4 py-7'>Il n'y pas de formation pour ce métier.</div>
                )}
              </div>
            </TabPanel>
          )}
        </TabsBody>
      </Tabs>
    </Fragment>
  );
};
